// src/features/form/PoliciesSettings.tsx
// Admin: form policies — defaults, approval, limits for a scope entity
// (organization, team, department), plus lookup by entity and context resolve.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useFormFormPolicy, useI18n } from '@react/hooks';
import { Alert, Badge, Button, Input, Textarea } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useScope } from '@/hooks/useScope';
import type { FormPolicy } from '@core/types/form';
import { FormModalShell } from './FormModalShell';
import { errMessage, makeEntity, parseClearableInt } from './helpers';

export const PoliciesSettings = observer(() => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useFormFormPolicy();

  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FormPolicy | null>(null);
  const [deleting, setDeleting] = useState<FormPolicy | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeType, setScopeType] = useState('team');
  const [scopeId, setScopeId] = useState('');
  const [allowAnonymous, setAllowAnonymous] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [maxFields, setMaxFields] = useState<string>('');

  const [lookupType, setLookupType] = useState('team');
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<FormPolicy | null | 'none'>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [resolveResult, setResolveResult] = useState<string | null>(null);
  const [resolveBusy, setResolveBusy] = useState(false);

  const load = () => {
    setError(null);
    store.ListFormPolicies().catch((e: unknown) => setError(errMessage(e, t('form.policies.loadError'))));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = async (listed: FormPolicy | null) => {
    let p = listed;
    if (listed?.entity?.id) {
      // Load the full policy — the list payload may be trimmed.
      try {
        p = await store.GetFormPolicy(listed.entity.id);
      } catch {
        p = listed;
      }
    }
    setEditing(p);
    setName(p?.name ?? '');
    setDescription(p?.description ?? '');
    setScopeType(p?.scope_entity?.type_name ?? p?.scope_entity_type ?? 'team');
    setScopeId(p?.scope_entity?.id ?? '');
    setAllowAnonymous(p?.default_allow_anonymous ?? false);
    setRequiresApproval(p?.require_submission_approval ?? false);
    setMaxFields(p?.max_fields_per_form ? String(p.max_fields_per_form) : '');
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editing?.entity?.id) {
        await store.UpdateFormPolicy(editing.entity.id, {
          policy: {
            ...editing,
            name: name.trim(),
            description,
            default_allow_anonymous: allowAnonymous,
            require_submission_approval: requiresApproval,
            max_fields_per_form: parseClearableInt(maxFields),
          },
          field_mask: ['name', 'description', 'default_allow_anonymous', 'require_submission_approval', 'max_fields_per_form'],
        });
      } else {
        await store.CreateFormPolicy({
          entity: makeEntity('', 'form_policy', scope),
          name: name.trim(),
          description: description || undefined,
          scope_entity: makeEntity(scopeId.trim(), scopeType.trim() || 'team', scope),
          scope_entity_type: scopeType.trim() || undefined,
          default_allow_anonymous: allowAnonymous,
          require_submission_approval: requiresApproval,
          max_fields_per_form: parseClearableInt(maxFields),
          allowed_template_ids: [],
        });
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(errMessage(e, t('form.policies.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const lookup = async () => {
    if (!lookupId.trim()) return;
    setLookupBusy(true);
    setLookupResult(null);
    try {
      const p = await store.GetFormPolicyByEntity(lookupType.trim() || 'team', lookupId.trim());
      setLookupResult(p ?? 'none');
    } catch {
      setLookupResult('none');
    } finally {
      setLookupBusy(false);
    }
  };

  const resolve = async () => {
    if (!lookupId.trim()) return;
    setResolveBusy(true);
    setResolveResult(null);
    try {
      const res = await store.ResolveFormContext({
        scope_entity: makeEntity(lookupId.trim(), lookupType.trim() || 'team', scope),
        scope_chain: [],
      });
      setResolveResult(res.effective_policy?.name ?? t('form.policies.resolveNone'));
    } catch {
      setResolveResult(t('form.policies.resolveNone'));
    } finally {
      setResolveBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('form.policies.hint')}</p>
        <Button size="sm" onClick={() => openDialog(null)}>{t('form.policies.newPolicy')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {store.isLoading && store.items.length === 0 && <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!store.isLoading && store.items.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('form.policies.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((p) => (
          <div key={p.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{p.name}</span>
                {p.scope_entity_type && <Badge variant="default">{p.scope_entity_type}</Badge>}
                {!p.is_active && <Badge variant="warning">{t('form.policies.inactive')}</Badge>}
              </div>
              {p.description && <p className="mt-0.5 truncate text-sm text-muted-foreground">{p.description}</p>}
            </div>
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => openDialog(p)}>{t('common.edit')}</Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(p)}>{t('common.delete')}</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2 rounded-md border border-border p-3">
        <span className="block text-sm font-medium text-foreground">{t('form.policies.lookupTitle')}</span>
        <div className="flex flex-wrap items-end gap-2">
          <Input label={t('form.policies.entityType')} value={lookupType} onChange={(e) => setLookupType(e.target.value)} fullWidth={false} className="w-36" />
          <Input label={t('form.policies.entityId')} value={lookupId} onChange={(e) => setLookupId(e.target.value)} fullWidth={false} className="w-72" />
          <Button size="sm" variant="secondary" onClick={lookup} isLoading={lookupBusy} disabled={!lookupId.trim()}>
            {t('form.policies.lookup')}
          </Button>
          <Button size="sm" variant="ghost" onClick={resolve} isLoading={resolveBusy} disabled={!lookupId.trim()}>
            {t('form.policies.resolve')}
          </Button>
        </div>
        {lookupResult === 'none' && <p className="text-sm text-muted-foreground">{t('form.policies.lookupNone')}</p>}
        {lookupResult && lookupResult !== 'none' && (
          <p className="text-sm text-foreground">{t('form.policies.lookupFound', { name: lookupResult.name })}</p>
        )}
        {resolveResult && <p className="text-sm text-foreground">{t('form.policies.resolveFound', { name: resolveResult })}</p>}
      </div>

      <FormModalShell
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('form.policies.editPolicy') : t('form.policies.newPolicy')}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)} disabled={isSaving}>{t('common.cancel')}</Button>
            <Button size="sm" onClick={save} isLoading={isSaving} disabled={!name.trim()}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <Textarea label={t('common.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          {!editing && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input label={t('form.policies.entityType')} value={scopeType} onChange={(e) => setScopeType(e.target.value)} placeholder="team" />
              <Input label={t('form.policies.entityId')} value={scopeId} onChange={(e) => setScopeId(e.target.value)} />
            </div>
          )}
          <Input
            label={t('form.policies.maxFieldsPerForm')}
            type="text"
            inputMode="numeric"
            value={maxFields}
            onChange={(e) => setMaxFields(e.target.value.replace(/[^0-9]/g, ''))}
          />
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={allowAnonymous} onChange={(e) => setAllowAnonymous(e.target.checked)} />
            {t('form.policies.defaultAllowAnonymous')}
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />
            {t('form.policies.requireApproval')}
          </label>
        </div>
      </FormModalShell>

      <ConfirmDialog
        open={!!deleting}
        title={t('form.policies.deleteTitle')}
        description={t('form.policies.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteFormPolicy(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('form.policies.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
